import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/utils'
import { PendingSpinner } from './pending-spinner'

/**
 * The quiet tier's one implementation, shared by `on-image-quiet` and `on-green-quiet`.
 * See the variant map below for why those two are the same treatment while their solid and
 * ghost siblings are not.
 */
const QUIET_ON_DARK =
  'bg-transparent text-white/70 hover:bg-white/10 hover:text-white focus-visible:ring-white dark:focus-visible:ring-white focus-visible:ring-offset-0'

// Focus ring uses the pelorous accent: pelorous-600 clears 3:1 contrast on the cream
// page in light mode; the brighter pelorous-400 takes over in dark mode where it pops
// against the near-black background. ring-offset-background keeps the 2px offset gap the
// same color as the page so the ring reads as a clean detached outline.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline: 'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        subtle: 'bg-brand-green-fill text-white shadow-sm hover:bg-brand-green-light',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // "Add another" tile: a dashed outline that reads as an empty slot waiting to be filled,
        // rather than a filled action competing with the real content above it. Pair with
        // `size="xl"` and `className="w-full"` for the full-width block form.
        //
        // The dark overrides are load-bearing, not polish — the same trap `PillButton`'s `add`
        // variant documents. `es-green-7` REMAPS to #334d2f in dark, which is ~2:1 on the page and
        // invisible; brand-green-400 is the readable step there. Any call site that puts an icon
        // badge inside should give it `text-current` so the icon follows this colour instead of
        // re-stating `text-es-green-7` and reintroducing the bug.
        dashed:
          'border-[1.5px] border-dashed border-input font-semibold text-brand-green-ink hover:border-brand-green-border hover:bg-card dark:hover:text-brand-green-300',
        // ── on-image / on-green tiers ───────────────────────────────────────────────
        // All five override the base ring COLOUR: pelorous is 1.6-2.9:1 against the brand greens
        // and unpredictable over a photo, under the 3:1 SC 1.4.11 wants of a focus indicator.
        // White is 7-21:1 on every surface these actually land on.
        //
        // They SPLIT on the offset, by whether the fill is opaque — the same split PillButton
        // already makes between `on-green-primary` and `on-green-secondary`:
        //
        //  • SOLID (white fill) keeps a 2px offset, recoloured to a dark band. A flush white ring
        //    on a white button is 1:1 against its own fill — literally invisible. The band makes
        //    the indicator SELF-CONTRASTING (white ring 15-21:1 vs the band, band 13-19:1 vs the
        //    white pill), so it reads without needing to know what's behind it. Deliberately NOT
        //    matched to the backdrop: there is no single colour to match — the green call sites
        //    range from #3d5a38 to black, and a photo can be anything.
        //  • GHOST / QUIET (transparent or translucent fill) go flush (`ring-offset-0`). A white
        //    ring already reads against their composited fill, and the base
        //    `ring-offset-background` would paint a 2px band of PAGE colour — measured as cream
        //    hsl(44 31% 88%) in light, near-black hsl(150 3% 7%) in dark — punched out of the
        //    image. Same call MealCard's over-photo controls make.
        // Both solid variants below are `bg-white` in BOTH modes, so their hover tint is a fixed
        // palette step rather than a theme-aware token. `hover:bg-es-green-1` flipped to a dark
        // green under `.dark` while the label stayed near-black — 1.07:1 here, 1.23:1 on
        // `on-green-solid` — so the label disappeared on hover. Same defect as #1112, which was
        // only filed against PillButton. `brand-green-100` is the closest fixed step to the old
        // light value, so light is unchanged.
        'on-image-solid':
          'bg-white text-[#22301d] hover:bg-brand-green-100 active:translate-y-px focus-visible:ring-white dark:focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-green-950',
        'on-image-ghost':
          'bg-white/[0.12] text-white border-[1.5px] border-white/35 backdrop-blur-[6px] hover:border-white hover:bg-white/20 active:translate-y-px focus-visible:ring-white dark:focus-visible:ring-white focus-visible:ring-offset-0',
        // The quiet third tier — no fill, no border, just a soft label. Sits below the ghost tier
        // in weight so a surface can carry three ranked actions (solid primary / ghost secondary /
        // quiet tertiary) without them competing.
        //
        // Both surfaces share ONE implementation, deliberately. The solid and ghost tiers really do
        // differ between image and green — ghost carries `backdrop-blur` and a stronger border over
        // a photo, and the solids use different label colours — because a photo is unpredictable
        // where flat green isn't. Quiet has no fill and no border, so nothing about it depends on
        // WHAT is behind, only that the backdrop is dark. Two names keep the `on-{surface}-{tier}`
        // matrix symmetric so callers find the one they expect; one constant means the pair cannot
        // drift into two subtly different quiet buttons.
        'on-image-quiet': QUIET_ON_DARK,
        'on-green-quiet': QUIET_ON_DARK,
        'on-green-solid':
          'bg-white text-brand-green-900 hover:bg-brand-green-100 active:translate-y-px focus-visible:ring-white dark:focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-green-900',
        'on-green-ghost':
          'bg-transparent text-white border-[1.5px] border-white/30 hover:border-white hover:bg-white/10 active:translate-y-px focus-visible:ring-white dark:focus-visible:ring-white focus-visible:ring-offset-0'
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        // Modal primary action: a taller 48px target on mobile (clears the 44px touch
        // minimum for full-width CTAs) that drops to the compact 36px default at md+.
        // Keeps the base pill radius.
        cta: 'h-12 px-4 py-2 md:h-9',
        // Block-scale action — a full-width tile rather than a control on a line. Height comes from
        // padding, not a fixed `h-*`, so it grows with its content, and the radius steps off the
        // base pill to a 2xl corner that matches the cards these sit among (`lg` sets its own
        // radius the same way).
        xl: 'h-auto gap-2.5 rounded-2xl px-6 py-5',
        icon: 'h-9 w-9'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /**
   * An action is in flight: prepends a spinner, disables the button, and marks it `aria-busy`.
   * A STATE rather than a variant — it composes with every variant and size.
   *
   * Ignored under `asChild`, where the child owns its own content.
   */
  pending?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, pending = false, children, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
          {children}
        </Slot>
      )
    }
    // `disabled` after the spread, deliberately: a caller passing `disabled: undefined` would
    // otherwise clobber it and leave a spinning button still clickable.
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        aria-busy={pending || undefined}
        disabled={pending || props.disabled}>
        {pending && <PendingSpinner />}
        {children}
      </button>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
